import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ManageBranchService } from "../api/services";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StudentCallsTransactionHistory = () => {
  const navigate = useNavigate();

  // --------------------------------------
  // STATE DECLARATIONS
  // --------------------------------------
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showPickerModal, setShowPickerModal] = useState(false);

  const [superAdmin_id, setSuperAdminId] = useState("");

  const [monthlyTransactions, setMonthlyTransactions] = useState([]); // raw monthly API
  const [dailyTransactions, setDailyTransactions] = useState([]); // daily filtered

  const [activeTab, setActiveTab] = useState("monthly");
  const [isLoading, setIsLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // --------------------------------------
  // HELPERS
  // --------------------------------------
  const [totalTransactions, setTotalTransactions] = useState(0);

  const getMonthLabel = (date) =>
    date.toLocaleString("default", { month: "long", year: "numeric" });

  const formatMonthYear = (date) => {
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${m}-${y}`;
  };

  const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  // --------------------------------------
  // FETCH MONTHLY API
  // --------------------------------------
  const fetchTransactionsForMonth = (userId, date) => {
    const payload = {
      user_id: userId,
      month: formatMonthYear(date),
    };

    setIsLoading(true);

    ManageBranchService.get_user_transactions(payload)
      .then((res) => {
        const monthly = res.data.results || [];
        // Calculate total CREDIT amount for the month
        const totalAmount = monthly.reduce((sum, tx) => {
          return sum + Number(tx.amount || 0);
        }, 0);


        setMonthlyTransactions(monthly);
        setTotalTransactions(totalAmount); 
        // Filter daily based on current selectedDate
        filterDailyTransactions(date, monthly);

        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Fetch failed:", err);
        setMonthlyTransactions([]);
        setDailyTransactions([]);
        setIsLoading(false);
      });
  };

  // --------------------------------------
  // FILTER DAILY
  // --------------------------------------
 const filterDailyTransactions = (day, source = monthlyTransactions) => {
  const filtered = monthlyTransactions.filter((tx) => {
    // FIX: parse date safely
    const txDate = new Date(tx.transaction_time.replace(" ", "T"));

    return (
      txDate.getFullYear() === day.getFullYear() &&
      txDate.getMonth() === day.getMonth() &&
      txDate.getDate() === day.getDate()
    );
  });

  setDailyTransactions(filtered);
  setCurrentPage(1);
};


  // --------------------------------------
  // CHANGE DAY (Prev / Next)
  // Always call monthly API from new day’s month
  // --------------------------------------
  const changeDay = (direction) => {
    debugger
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);

    setSelectedDate(newDate);

    // Call API every time day changes
    fetchTransactionsForMonth(superAdmin_id, newDate);
  };

  // --------------------------------------
  // CHANGE MONTH
  // --------------------------------------
  const handleMonthChange = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);

    setSelectedMonth(newDate);
    fetchTransactionsForMonth(superAdmin_id, newDate);
  };

  // --------------------------------------
  // ON LOAD
  // --------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) navigate("/");

    const userId = localStorage.getItem("admin_id");
    setSuperAdminId(userId);

    fetchTransactionsForMonth(userId, selectedMonth);
  }, []);

  // --------------------------------------
  // PAGINATION
  // --------------------------------------
  const lastIndex = currentPage * recordsPerPage;
  const firstIndex = lastIndex - recordsPerPage;
  const paginatedDaily = dailyTransactions.slice(firstIndex, lastIndex);
  const totalPages = Math.ceil(dailyTransactions.length / recordsPerPage);

  // --------------------------------------
  // MONTH PICKER MODAL
  // --------------------------------------
  const renderDatepickerModal = () =>
    showPickerModal && (
      <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Select Month</h5>
              <button className="btn-close" onClick={() => setShowPickerModal(false)} />
            </div>

            <div className="modal-body text-center">
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                inline
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPickerModal(false)}>
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  fetchTransactionsForMonth(superAdmin_id, selectedMonth);
                  setShowPickerModal(false);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  // --------------------------------------
  // UI
  // --------------------------------------
  return (
    <div className="container mt-4">
      {/* Loader */}
      {isLoading && (
        <div className="loader">
          <div className="lds-spinner">
            {Array(12)
              .fill()
              .map((_, i) => (
                <div key={i}></div>
              ))}
          </div>
        </div>
      )}

      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "monthly" ? "active" : ""}`}
            onClick={() => setActiveTab("monthly")}
          >
            Monthly Summary
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "daily" ? "active" : ""}`}
            onClick={() => setActiveTab("daily")}
          >
            Daily Summary
          </button>
        </li>
      </ul>

      {/* MONTHLY TAB */}
      {activeTab === "monthly" && (
        <>
          <div className="bg-warning text-white px-4 py-3 rounded-top d-flex justify-content-between">
            <h5 className="mb-0">Transactions (Monthly)</h5>
          </div>

          <div className="bg-warning d-flex justify-content-center align-items-center py-2 gap-3 rounded-bottom">
            <button className="btn btn-outline-light rounded-circle border-0" onClick={() => handleMonthChange(-1)}>
              <FaArrowLeft />
            </button>

            <span className="fw-bold text-white" onClick={() => setShowPickerModal(true)}>
              {getMonthLabel(selectedMonth)}
            </span>

            <button className="btn btn-outline-light rounded-circle border-0" onClick={() => handleMonthChange(1)}>
              <FaArrowRight />
            </button>
          </div>
           <div className="text-center py-2">
              <h6 className="fw-bold">
                Transactions {monthlyTransactions.length} of {totalTransactions}{" "}
                <span className="text-success">↑</span>
              </h6>
            </div>

          {/* Monthly Table */}
          <div className="table-responsive shadow-sm border rounded-bottom bg-white">
            <table className="table table-bordered text-center mb-0">
              <thead className="table-warning">
                <tr>
                  <th>Date</th>
                  <th>Transaction ID</th>
                  <th>By</th>
                  <th>To</th>
                  <th>Amount</th>
                  <th>Type</th>
                </tr>
              </thead>

              <tbody>
                {monthlyTransactions.length > 0 ? (
                  monthlyTransactions.map((txn) => (
                    <tr key={txn.transaction_id}>
                      <td>{txn.transaction_time}</td>
                      <td>{txn.transaction_id}</td>
                      <td>{txn.by_name}</td>
                      <td>{txn.to_name}</td>
                      <td>{txn.amount}</td>
                      <td>{txn.transaction_type}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">No transactions found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* DAILY TAB */}
      {activeTab === "daily" && (
        <>
          <div className="bg-primary text-white px-4 py-3 rounded-top d-flex justify-content-between">
            <h5 className="mb-0">Daily Summary</h5>
          </div>

          {/* Daily Controls */}
          <div className="bg-primary text-white d-flex justify-content-center align-items-center py-2 gap-3 rounded-bottom">
            <button className="btn btn-outline-light rounded-circle border-0" onClick={() => changeDay(-1)}>
              <FaArrowLeft />
            </button>

            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                fetchTransactionsForMonth(superAdmin_id, date);
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control w-auto text-center fw-bold"
            />

            <button className="btn btn-outline-light rounded-circle border-0" onClick={() => changeDay(1)}>
              <FaArrowRight />
            </button>
          </div>

          {/* Daily Table */}
          <div className="table-responsive shadow-sm border rounded-bottom bg-white">
            <table className="table table-bordered text-center mb-0">
              <thead className="table-primary">
                <tr>
                  <th>Time</th>
                  <th>Transaction ID</th>
                  <th>By</th>
                  <th>To</th>
                  <th>Amount</th>
                  <th>Type</th>
                </tr>
              </thead>

              <tbody>
                {paginatedDaily.length > 0 ? (
                  paginatedDaily.map((txn) => (
                    <tr key={txn.transaction_id}>
                      <td>{txn.transaction_time}</td>
                      <td>{txn.transaction_id}</td>
                      <td>{txn.by_name}</td>
                      <td>{txn.to_name}</td>
                      <td>{txn.amount}</td>
                      <td>{txn.transaction_type}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">No transactions found for this date.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {dailyTransactions.length > 10 && (
            <div className="d-flex justify-content-center mt-3">
              <button
                className="btn btn-outline-primary mx-2"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Prev
              </button>

              <span className="fw-bold">
                {currentPage} / {totalPages}
              </span>

              <button
                className="btn btn-outline-primary mx-2"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {renderDatepickerModal()}
    </div>
  );
};

export default StudentCallsTransactionHistory;
